import React, { useState, useEffect } from "react";
import { Search, Eye, Edit, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "antd";

import {
  getClientsByPaymentType,
  updateCreditAccStatus,
} from "../../../Store/Apis/ReservationApi";
const ViewAccount = () => {
  const [creditAccounts, setCreditAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch accounts with payment type ID 10001 (Account Payment)
  useEffect(() => {
    setLoading(true);
    getClientsByPaymentType(10001, 4)
      .then((res) => {
        if (res?.status === 200 && Array.isArray(res.data)) {
          console.log("ViewAccount - Total API response:", res.data.length);
          console.log("ViewAccount - All records:", res.data);

          // Filter accounts to show those with exceed_Limit: "No" OR "confirmed"
          const filteredData = res.data.filter((c) => {
            console.log(
              `Record ${c.clientID}: exceed_Limit = "${c.exceed_Limit}"`
            );
            return c.exceed_Limit === "No" || c.exceed_Limit === "confirmed";
          });

          console.log(
            "ViewAccount - After filtering (exceed_Limit = 'No'):",
            filteredData.length
          );

          // Map API response to table structure
          const mapped = filteredData.map((c) => ({
            id: c.clientID,
            customerName: c.name,
            email: c.email,
            phoneNumber: c.phone,
            creditStartDate: c.createdAt, // using createdAt as start date fallback
            agreedCreditAmount: c.creditLimit,
            paymentTerms: c.paymentTerms,
            status:
              c.creditAccStatus || c.status || c.accountStatus || "Active",
            exceed_Limit: c.exceed_Limit || "No", // Include exceed_Limit from API response
          }));
          setCreditAccounts(mapped);
        } else {
          setError("Failed to load accounts");
        }
      })
      .catch(() => setError("Failed to load accounts"))
      .finally(() => setLoading(false));
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter accounts based on search term
  const filteredAccounts = creditAccounts.filter(
    (account) =>
      account.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.phoneNumber.includes(searchTerm)
  );

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination for current page
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = filteredAccounts.slice(startIndex, endIndex);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const navigate = useNavigate();
  const handleAddAccount = () => {
    navigate("/credit/add-account");
  };

  const handleView = (accountId) => {
    navigate(`/credit/view/${accountId}`);
  };

  const handleEdit = (accountId) => {
    navigate(`/credit/add-account`);
  };

  const handleToggleStatus = async (account) => {
    const nextStatus =
      account.status?.toLowerCase() === "on hold" ? "Active" : "on hold";
    try {
      // optimistic update
      setCreditAccounts((prev) =>
        prev.map((a) =>
          a.id === account.id ? { ...a, status: nextStatus } : a
        )
      );
      const payload = {
        clientId: account.id,
        newStatus: nextStatus,
        exceed_Limit: account.exceed_Limit,
      };

      console.log(
        "Debug - Payload being sent to updateCreditAccStatus:",
        payload
      );
      console.log("Debug - Full account object:", account);

      const res = await updateCreditAccStatus(payload);
      if (!(res?.status >= 200 && res?.status < 300)) {
        // revert on failure
        setCreditAccounts((prev) =>
          prev.map((a) =>
            a.id === account.id ? { ...a, status: account.status } : a
          )
        );
        alert(res?.message || "Failed to update status");
      }
    } catch (e) {
      // revert on error
      setCreditAccounts((prev) =>
        prev.map((a) =>
          a.id === account.id ? { ...a, status: account.status } : a
        )
      );
      alert("Failed to update status");
    }
  };

  return (
    <div className="card p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Credit Accounts</h5>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={handleAddAccount}
        >
          <Plus size={16} />
          Add Credit Account
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="position-relative">
          <Search
            className="position-absolute"
            style={{
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#6c757d",
            }}
            size={16}
          />
          <input
            type="text"
            className="form-control ps-5"
            placeholder="Search by customer name, email, or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col">Customer Name</th>
              <th scope="col">Email</th>
              <th scope="col">Phone Number</th>
              <th scope="col">Credit Start Date</th>
              <th scope="col">Credit Amount</th>
              <th scope="col">Payment Terms</th>
              <th scope="col">Status</th>
              <th scope="col" className="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="8" className="text-center text-danger py-4">
                  {error}
                </td>
              </tr>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((account) => (
                <tr key={account.id}>
                  <td className="fw-medium">{account.customerName}</td>
                  <td className="text-muted">{account.email}</td>
                  <td className="text-muted">{account.phoneNumber}</td>
                  <td>{formatDate(account.creditStartDate)}</td>
                  <td className="fw-medium text-success">
                    R {account.agreedCreditAmount}
                  </td>
                  <td>
                    <span className="badge bg-info text-dark">
                      {account.paymentTerms}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge rounded-pill ${
                        account.status?.toLowerCase() === "on hold"
                          ? "bg-warning text-dark"
                          : "bg-success"
                      }`}
                    >
                      {account.status}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2 justify-content-center">
                      {/* <button
                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                        onClick={() => handleView(account.id)}
                        title="View Account"
                      >
                        <Eye size={14} />
                        View
                      </button> */}
                      <button
                        className="btn btn-outline-warning btn-sm d-flex align-items-center gap-1"
                        onClick={() => handleToggleStatus(account)}
                        title={
                          account.status?.toLowerCase() === "on hold"
                            ? "Set Active"
                            : "Put On Hold"
                        }
                      >
                        {account.status?.toLowerCase() === "on hold"
                          ? "Set Active"
                          : "Put On Hold"}
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={() => handleEdit(account.id)}
                        title="Edit Account"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  {searchTerm
                    ? "No accounts found matching your search."
                    : "No credit accounts found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Ant Design Pagination */}
      {!loading && !error && filteredAccounts.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted small">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredAccounts.length)} of{" "}
            {filteredAccounts.length} credit accounts
            {searchTerm && ` (filtered from ${creditAccounts.length} total)`}
          </div>

          <Pagination
            current={currentPage}
            total={filteredAccounts.length}
            pageSize={pageSize}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            showSizeChanger={true}
            showQuickJumper={true}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            pageSizeOptions={["5", "10", "20", "50", "100"]}
            size="default"
          />
        </div>
      )}
    </div>
  );
};

export default ViewAccount;
